// VisorPdf.js — Muestra un PDF dentro del reproductor del modo Mentor.
//
// Se dibuja con PDF.js en lugar del visor del navegador: en muchos celulares
// (Android sobre todo) un PDF dentro de un iframe sale en blanco. Cada página se
// pinta al ancho del recuadro y solo cuando se acerca a la vista, así un documento
// largo no se carga de golpe.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import styled from 'styled-components';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf';

// El worker se sirve desde public/ (copia de pdfjs-dist/legacy/build/pdf.worker.min.js).
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

const Marco = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background: #3b3f45;
`;

const Barra = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.7rem;
  background: #24272b;
  color: #d6d9de;
  font-size: 0.82rem;
  flex-wrap: wrap;
  button, a {
    border: 0;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
    font: inherit;
    padding: 0.3rem 0.6rem;
    cursor: pointer;
    text-decoration: none;
    line-height: 1.2;
  }
  button:hover, a:hover { background: rgba(255, 255, 255, 0.16); }
  button:focus-visible, a:focus-visible { outline: 2px solid #14b6cb; outline-offset: 1px; }
  .sep { flex: 1; }
`;

const Paginas = styled.div`
  flex: 1;
  overflow: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  -webkit-overflow-scrolling: touch;
`;

const Hoja = styled.div`
  position: relative;
  background: #fff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.35);
  flex: none;
  canvas { display: block; width: 100%; height: 100%; }
`;

const Aviso = styled.div`
  margin: auto;
  color: #d6d9de;
  font-size: 0.9rem;
  text-align: center;
  padding: 1rem;
  a { color: #5fd3e0; }
`;

// Una página: reserva su alto desde el principio y se pinta al entrar en vista.
const Pagina = ({ pdf, numero, ancho, raiz }) => {
  const ref = useRef(null);
  const canvasRef = useRef(null);
  const [tam, setTam] = useState(null);      // {w, h} en px CSS
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    let vivo = true;
    pdf.getPage(numero).then(pg => {
      if (!vivo) return;
      const v = pg.getViewport({ scale: 1 });
      setTam({ w: ancho, h: Math.round(ancho * v.height / v.width) });
    });
    return () => { vivo = false; };
  }, [pdf, numero, ancho]);

  useEffect(() => {
    const el = ref.current;
    if (!el || !raiz) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); },
      { root: raiz, rootMargin: '600px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, [raiz]);

  useEffect(() => {
    if (!visible || !tam) return undefined;
    let tarea = null; let vivo = true;
    pdf.getPage(numero).then(pg => {
      if (!vivo) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const base = pg.getViewport({ scale: 1 });
      const vp = pg.getViewport({ scale: (tam.w / base.width) * dpr });
      const c = canvasRef.current;
      if (!c) return;
      c.width = Math.floor(vp.width);
      c.height = Math.floor(vp.height);
      tarea = pg.render({ canvasContext: c.getContext('2d'), viewport: vp });
      tarea.promise.catch(() => {});
    });
    return () => { vivo = false; if (tarea) tarea.cancel(); };
  }, [visible, tam, pdf, numero]);

  return (
    <Hoja ref={ref} style={tam ? { width: tam.w, height: tam.h } : { width: ancho, height: Math.round(ancho * 1.3) }}
      aria-label={`Página ${numero}`}>
      {visible && <canvas ref={canvasRef} />}
    </Hoja>
  );
};

// descargar=false: sin el botón «Descargar» (Biblioteca de medios → «Permitir descargar»).
const VisorPdf = ({ url, titulo, descargar = true }) => {
  const contRef = useRef(null);
  const [raiz, setRaiz] = useState(null);
  const [pdf, setPdf] = useState(null);
  const [error, setError] = useState(false);
  const [anchoDisp, setAnchoDisp] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [actual, setActual] = useState(1);

  useEffect(() => {
    let vivo = true; let doc = null;
    setPdf(null); setError(false); setActual(1); setZoom(1);
    const tarea = pdfjsLib.getDocument({ url, withCredentials: false });
    tarea.promise.then(d => { doc = d; if (vivo) setPdf(d); }).catch(() => vivo && setError(true));
    return () => { vivo = false; tarea.destroy(); if (doc) doc.destroy(); };
  }, [url]);

  // Ancho disponible del recuadro (cambia al girar el celular o abrir paneles).
  useEffect(() => {
    const el = contRef.current;
    if (!el) return undefined;
    const medir = () => setAnchoDisp(el.clientWidth);
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const ancho = Math.max(200, Math.round(Math.min(anchoDisp - 24, 900) * zoom));
  const setCont = useCallback((el) => { contRef.current = el; setRaiz(el); }, []);

  // Página actual: la que ocupa el tercio superior del recuadro (una hoja más
  // alta que la pantalla del celular nunca se ve entera).
  const alDesplazar = (e) => {
    const cont = e.currentTarget;
    const linea = cont.scrollTop + cont.clientHeight / 3;
    let n = 1;
    Array.from(cont.children).forEach((h, i) => { if (h.offsetTop <= linea) n = i + 1; });
    setActual(n);
  };

  return (
    <Marco>
      <Barra>
        <span>{pdf ? `Página ${actual} de ${pdf.numPages}` : 'Cargando documento…'}</span>
        <span className="sep" />
        <button type="button" onClick={() => setZoom(z => Math.max(0.5, +(z - 0.25).toFixed(2)))} aria-label="Alejar">−</button>
        <span>{Math.round(zoom * 100)} %</span>
        <button type="button" onClick={() => setZoom(z => Math.min(3, +(z + 0.25).toFixed(2)))} aria-label="Acercar">+</button>
        <a href={url} target="_blank" rel="noopener noreferrer">Abrir</a>
        {descargar && <a href={url} download>Descargar</a>}
      </Barra>
      <Paginas ref={setCont} onScroll={alDesplazar} aria-label={titulo || 'Documento PDF'}>
        {error ? (
          <Aviso>
            No se pudo mostrar el documento aquí.<br />
            <a href={url} target="_blank" rel="noopener noreferrer">Ábrelo en otra pestaña</a>.
          </Aviso>
        ) : pdf && anchoDisp > 0 ? (
          Array.from({ length: pdf.numPages }, (_, i) => (
            <Pagina key={`${url}-${i}`} pdf={pdf} numero={i + 1} ancho={ancho} raiz={raiz} />
          ))
        ) : null}
      </Paginas>
    </Marco>
  );
};

export default VisorPdf;
